import { useReactFlow, useStore, type Rect } from '@xyflow/react';
import type { CSSProperties } from 'react';

import { IconButton } from '@commenda/pathfinder';
import { Expand06Icon } from '@commenda/pathfinder/icons/Expand06Icon';
import { MinusIcon } from '@commenda/pathfinder/icons/MinusIcon';
import { PlusIcon } from '@commenda/pathfinder/icons/PlusIcon';

import {
  ReadableViewportHelper,
  useReadableFitView,
} from '../../ChartViewport';
import { StructureCanvasHelper, type StructureEntityFlowNode } from '../canvas';
import { floatingControlStyle } from './floating-control-style';

interface StructureZoomControlsProps {
  flowNodes: ReadonlyArray<StructureEntityFlowNode>;
  contentBounds: Readonly<Rect>;
  nodeSize: Readonly<{ width: number; height: number }>;
  minZoom: number;
}

const ZOOM_STEP_MS = 200;
const FIT_MS = 250;
// Zoom lands within a hair of its limits, so the buttons compare with some slack.
const ZOOM_SLACK = 0.001;

const controlsStyle: CSSProperties = {
  ...floatingControlStyle,
  left: '50%',
  transform: 'translateX(-50%)',
  gap: 2,
};

const zoomLevelStyle: CSSProperties = {
  width: 44,
  textAlign: 'center',
  font: '500 12px/16px var(--pf-font-body)',
  color: 'var(--pf-color-soft-pearl-900)',
};

const dividerStyle: CSSProperties = {
  width: 0.5,
  height: 16,
  background: 'var(--pf-color-soft-pearl-300)',
};

// Must render inside <ReactFlowProvider>.
const StructureZoomControls = ({
  flowNodes,
  contentBounds,
  nodeSize,
  minZoom,
}: StructureZoomControlsProps) => {
  const { zoomIn, zoomOut } = useReactFlow();
  const zoom = useStore((s) => s.transform[2]);
  const fitView = useReadableFitView({
    nodes: flowNodes,
    nodeSize,
    minZoom,
    contentBounds,
  });

  return (
    <div style={controlsStyle}>
      <IconButton
        label="Zoom out"
        size="sm"
        disabled={zoom <= ReadableViewportHelper.MANUAL_MIN_ZOOM + ZOOM_SLACK}
        onClick={() =>
          zoomOut({ duration: StructureCanvasHelper.getMotionMs(ZOOM_STEP_MS) })
        }
      >
        <MinusIcon />
      </IconButton>
      <span style={zoomLevelStyle}>{Math.round(zoom * 100)}%</span>
      <IconButton
        label="Zoom in"
        size="sm"
        disabled={zoom >= ReadableViewportHelper.MAX_ZOOM - ZOOM_SLACK}
        onClick={() =>
          zoomIn({ duration: StructureCanvasHelper.getMotionMs(ZOOM_STEP_MS) })
        }
      >
        <PlusIcon />
      </IconButton>
      <span aria-hidden="true" style={dividerStyle} />
      <IconButton
        label="Fit to screen"
        size="sm"
        onClick={() =>
          fitView({ duration: StructureCanvasHelper.getMotionMs(FIT_MS) })
        }
      >
        <Expand06Icon />
      </IconButton>
    </div>
  );
};

export { StructureZoomControls };
